// State of the backing track and of following the player through the song.
// Lives in the practice dock rather than in a panel, so the music keeps going
// (and the chord marker keeps moving through the lyrics) when the panel is
// closed, and so the chords panel can show where the player is.
//
// What moves the marker (the "mode"):
//   - while the band plays: the backing track's source, 'listen' (the
//     microphone), 'tap' (keys, pedal or the chord buttons) or 'auto' (the
//     chords move on by themselves every so many bars);
//   - when following without the band: 'listen' or 'tap';
//   - otherwise 'off'.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { chordSequence, parseChord } from './chords';
import BackingTrackEngine from './engine';
import { NEXT_VELOCITY, PRESETS, emptyGrid } from './patterns';
import JamSession from './session';
import { track } from '../../usage';
import { resumeAudio } from '../practice-dock/audio';
import { loadStored, saveStored } from '../practice-dock/storage';
import { clearSelection, markChords, markSection, scrollIntoView, selectedSection } from '../practice-dock/song-dom';
import { isShortcut } from '../practice-dock/shortcuts';

const STORAGE_KEY = 'gp-backing-track';
// How long chords last when they move on by themselves, in bars.
export const CHORD_BARS = [0.5, 1, 2];
// Don't scroll the song while the player is scrolling it themselves.
const MANUAL_SCROLL_MS = 4000;

const SOURCES = ['listen', 'tap', 'auto'];

function initialSettings() {
    const stored = loadStored(STORAGE_KEY) || {};
    const presetId = PRESETS.some(p => p.id === stored.presetId) ? stored.presetId : 'pop';
    return {
        presetId,
        custom: stored.custom || {},
        drums: stored.drums ?? 0.8,
        bass: stored.bass ?? 0.8,
        headphones: !!stored.headphones,
        drumSound: stored.drumSound === 'synth' ? 'synth' : 'acoustic',
        source: SOURCES.includes(stored.source) ? stored.source : 'listen',
        followSource: stored.followSource === 'tap' ? 'tap' : 'listen',
        chordBars: CHORD_BARS.includes(stored.chordBars) ? stored.chordBars : 1,
    };
}

// Current groove: the preset with any changes the user made to it.
function groove(presetId, customised) {
    const preset = PRESETS.find(p => p.id === presetId);
    const custom = customised[preset.id] || {};
    return {
        preset: { ...preset, swing: custom.swing ?? preset.swing },
        grid: custom.grid || emptyGrid(preset),
        bassStyle: custom.bass || preset.bass,
        bassSound: custom.bassSound || preset.bassSound,
        modified: !!(custom.grid || custom.bass || custom.bassSound || custom.swing !== undefined),
    };
}

// transport: the dock's clock (see practice-dock/use-transport). The band,
// and following by listening, are for subscribers; following by tapping is
// open to everyone. capo: the fret the player's capo is on, 0 for none (see
// JamSession).
export default function useBackingTrack(song, { transport, isSubscriber, onRequireSubscription, capo = 0 }) {
    const { engine } = transport;
    const [playing, setPlaying] = useState(false);
    const [loading, setLoading] = useState(false);
    const [following, setFollowing] = useState(false);
    const [settings, setSettings] = useState(initialSettings);
    const [live, setLive] = useState({ chord: null, next: null, position: -1, nextPosition: -1, micState: 'off', level: 0 });
    const [section, setSection] = useState(null);
    const [selection, setSelection] = useState(null);
    const [passes, setPasses] = useState(0);
    const sessionRef = useRef(null);
    const touchedRef = useRef(false);
    const followingRef = useRef(false);
    const manualScrollRef = useRef(0);
    // Read when the session is made; changes go to it (see below) without
    // losing the place in the song.
    const capoRef = useRef(capo);
    capoRef.current = capo;
    // Bumped by stop(), so a start still waiting on the microphone or the
    // samples knows it was cancelled.
    const runRef = useRef(0);

    const { preset, grid, bassStyle, bassSound, modified } = useMemo(() => groove(settings.presetId, settings.custom), [settings.presetId, settings.custom]);
    const sequence = useMemo(() => chordSequence(song.body_chordified), [song.body_chordified]);
    const chordNames = useMemo(() => sequence.filter(name => parseChord(name)), [sequence]);
    const songChords = useMemo(() => chordNames.filter((name, i) => chordNames.indexOf(name) === i), [chordNames]);

    const getSession = useCallback(() => {
        if (!sessionRef.current) {
            sessionRef.current = new JamSession({ engine, capo: capoRef.current, onChange: setLive, onPass: () => setPasses(p => p + 1) });
            sessionRef.current.setSong(sequence);
        }
        return sessionRef.current;
    }, [engine, sequence]);

    const mode = playing ? settings.source : following ? settings.followSource : 'off';
    const active = mode !== 'off';

    const stop = useCallback(() => {
        runRef.current += 1;
        transport.stopBand();
        setPlaying(false);
        setLoading(false);
        if (!followingRef.current && sessionRef.current) {
            sessionRef.current.stopListening(); // in case a start was waiting on the microphone
        }
    }, [transport.stopBand]); // eslint-disable-line react-hooks/exhaustive-deps

    const stopFollowing = useCallback(() => {
        followingRef.current = false;
        setFollowing(false);
    }, []);

    useEffect(() => () => {
        if (sessionRef.current) sessionRef.current.close();
    }, []);

    // A new song (next song in a songbook) stops the band and forgets the loop.
    useEffect(() => {
        stop();
        stopFollowing();
        setSection(null);
        setPasses(0);
    }, [song.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Transposing changes the chords mid-song.
    useEffect(() => {
        if (sessionRef.current) sessionRef.current.setSong(sequence);
    }, [sequence]);

    useEffect(() => {
        if (sessionRef.current) sessionRef.current.setCapo(capo);
    }, [capo]);

    useEffect(() => {
        if (touchedRef.current) saveStored(STORAGE_KEY, settings);
    }, [settings]);

    // Push setting changes into the engine.
    useEffect(() => {
        engine.setPattern(preset, grid);
    }, [engine, preset, grid]);
    useEffect(() => {
        engine.setBassStyle(bassStyle);
    }, [engine, bassStyle]);
    useEffect(() => {
        engine.setVolumes({ drums: settings.drums, bass: settings.bass });
    }, [engine, settings.drums, settings.bass]);
    useEffect(() => {
        engine.setSounds({ drums: settings.drumSound, bass: bassSound });
    }, [engine, settings.drumSound, bassSound]);

    // Point the session at whatever moves the marker now.
    const autoBeats = Math.max(1, Math.round(settings.chordBars * preset.beats));
    useEffect(() => {
        if (mode === 'off' && !sessionRef.current) return;
        const session = getSession();
        if (mode === 'listen') {
            if (!session.listening || session.headphones !== settings.headphones) {
                session.startListening({ headphones: settings.headphones });
            }
        } else {
            session.stopListening();
        }
        session.setAuto(mode === 'auto' ? autoBeats : null);
        if (mode === 'tap' && session.current === null) {
            session.step(1);
        }
    }, [mode, settings.headphones, autoBeats, getSession]);

    // Loop: the session moves only within the part, and the part is shaded.
    useEffect(() => {
        if (sessionRef.current || section) getSession().setSection(section);
        setPasses(0);
        markSection(section);
    }, [section, getSession]);
    useEffect(() => () => markSection(null), []);

    // Remember what's selected in the song, for "Loop the selected lines":
    // tapping the button can clear the selection before the click lands.
    useEffect(() => {
        const onSelectionChange = () => {
            const next = selectedSection();
            if (next) {
                setSelection(current => (current && current.start === next.start && current.end === next.end ? current : next));
            }
        };
        document.addEventListener('selectionchange', onSelectionChange);
        return () => document.removeEventListener('selectionchange', onSelectionChange);
    }, []);

    useEffect(() => {
        const onManualScroll = () => {
            manualScrollRef.current = performance.now();
        };
        window.addEventListener('wheel', onManualScroll, { passive: true });
        window.addEventListener('touchmove', onManualScroll, { passive: true });
        return () => {
            window.removeEventListener('wheel', onManualScroll);
            window.removeEventListener('touchmove', onManualScroll);
        };
    }, []);

    // Mark the chord the player is on in the lyrics, and keep it in view
    // above the dock.
    useEffect(() => {
        const el = markChords(active ? live.position : -1, active ? live.nextPosition : -1);
        if (el && performance.now() - manualScrollRef.current > MANUAL_SCROLL_MS) {
            scrollIntoView(el);
        }
    }, [live.position, live.nextPosition, active, song.body_chordified]);
    useEffect(() => () => markChords(-1, -1), []);

    // Tap mode: arrow keys and page-turner pedals step through the song.
    useEffect(() => {
        if (mode !== 'tap') return undefined;
        const onKey = e => {
            if (!isShortcut(e)) return;
            const session = sessionRef.current;
            if (['ArrowRight', 'PageDown'].includes(e.key)) {
                session.step(1);
            } else if (['ArrowLeft', 'PageUp'].includes(e.key)) {
                session.step(-1);
            } else if (/^[1-9]$/.test(e.key) && session.names[Number(e.key) - 1]) {
                session.selectByName(session.names[Number(e.key) - 1]);
            } else {
                return;
            }
            e.preventDefault();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mode]);

    const update = changes => {
        touchedRef.current = true;
        setSettings(current => ({ ...current, ...changes }));
    };

    const updateGroove = changes => {
        touchedRef.current = true;
        setSettings(current => ({
            ...current,
            custom: { ...current.custom, [current.presetId]: { ...(current.custom[current.presetId] || {}), ...changes } },
        }));
    };

    const resetGroove = () => {
        touchedRef.current = true;
        setSettings(current => {
            const custom = { ...current.custom };
            delete custom[current.presetId];
            return { ...current, custom };
        });
    };

    // Fetch the recorded drums and bass when the panel opens, before Play.
    const preload = () => {
        if (!isSubscriber) return;
        engine.setSounds({ drums: settings.drumSound, bass: bassSound });
        engine.loadSounds();
    };

    const start = async () => {
        if (!isSubscriber) {
            onRequireSubscription();
            return;
        }
        touchedRef.current = true;
        const run = ++runRef.current;
        const session = getSession();
        // Resume audio inside the click so browsers allow it to play.
        resumeAudio();
        // From the top (or the top of the loop), unless the player is
        // already being followed somewhere in the song.
        if (!followingRef.current || settings.source !== settings.followSource) {
            session.restart();
        }
        setPasses(0);
        setLoading(true);
        // Ask for the microphone before the count-in starts.
        if (settings.source === 'listen' && !(session.listening && session.headphones === settings.headphones)) {
            await session.startListening({ headphones: settings.headphones });
        }
        if (run !== runRef.current) return;
        setPlaying(true);
        await transport.startBand();
        if (run !== runRef.current) {
            transport.stopBand();
            return;
        }
        setLoading(false);
        track('backing_track_started', { source: settings.source });
    };

    // Follow the player through the song without the band.
    const startFollowing = () => {
        if (settings.followSource === 'listen' && !isSubscriber) {
            onRequireSubscription();
            return;
        }
        resumeAudio();
        getSession().restart();
        followingRef.current = true;
        setFollowing(true);
        track('follow_started', { source: settings.followSource });
    };

    const setFollowSource = followSource => {
        if (followSource === 'listen' && following && !isSubscriber) {
            onRequireSubscription();
            return;
        }
        update({ followSource });
    };

    const setSource = source => update({ source });
    const setHeadphones = headphones => update({ headphones });

    const toggleCell = (voice, step) => {
        const row = grid[voice];
        const next = row.slice(0, step) + NEXT_VELOCITY[row[step]] + row.slice(step + 1);
        updateGroove({ grid: { ...grid, [voice]: next } });
    };

    const selectChord = name => {
        if (mode === 'tap' && sessionRef.current) sessionRef.current.selectByName(name);
    };

    // Tap mode: the previous or next chord in the song.
    const step = delta => {
        if (mode === 'tap' && sessionRef.current) sessionRef.current.step(delta);
    };

    const loopSelection = () => {
        const next = selectedSection() || selection;
        if (!next) return;
        setSection(next);
        setSelection(null);
        clearSelection();
        track('practice_loop_set');
    };

    const clearLoop = () => setSection(null);

    return {
        available: songChords.length > 0 && BackingTrackEngine.isSupported(),
        isSubscriber,
        requireSubscription: onRequireSubscription,
        playing,
        loading,
        following,
        mode,
        active,
        settings,
        live,
        beat: transport.beat,
        bpm: transport.bpm,
        preset,
        grid,
        bassStyle,
        bassSound,
        modified,
        songChords,
        chordNames,
        section,
        selection,
        passes,
        start,
        stop,
        startFollowing,
        stopFollowing,
        preload,
        update,
        updateGroove,
        resetGroove,
        setSource,
        setFollowSource,
        setHeadphones,
        tapTempo: transport.tapTempo,
        nudgeTempo: transport.nudgeTempo,
        toggleCell,
        selectChord,
        step,
        loopSelection,
        clearLoop,
    };
}
